import React, { Suspense, lazy, useMemo, useEffect } from 'react'
import { detectNoteType, getEditorComponentName } from '../../utils/noteTypeDetection'

// Lazy load editor components
const FlowchartNoteEditor = lazy(() => import('./FlowchartNoteEditor'))
const HandwritingNoteEditor = lazy(() => import('./HandwritingNoteEditor'))
const VoiceNoteEditor = lazy(() => import('./VoiceNoteEditor'))
const ItemListNoteEditor = lazy(() => import('./ItemListNoteEditor'))
const PhotoNoteEditor = lazy(() => import('./PhotoNoteEditor'))
const SpreadsheetNoteEditor = lazy(() => import('./SpreadsheetNoteEditor'))

/**
 * Loading skeleton component for editors
 */
const EditorLoadingSkeleton = () => (
  <div className="h-full flex flex-col animate-pulse">
    {/* Title skeleton */}
    <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded mb-4"></div>
    
    {/* Content skeleton */}
    <div className="flex-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-6">
      <div className="space-y-4">
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6"></div>
      </div>
    </div>
    
    {/* Action buttons skeleton */}
    <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
      <div className="h-10 w-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
      <div className="h-10 w-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
    </div>
  </div>
)

/**
 * Error boundary component for editor loading errors
 */
class EditorErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  componentDidCatch(error, errorInfo) {
    console.error('Editor loading error:', error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="h-full flex flex-col items-center justify-center p-6">
          <div className="text-center">
            <div className="text-red-500 text-6xl mb-4">⚠️</div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              Failed to Load Editor
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              There was an error loading the editor for this note type.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Reload Page
            </button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

/**
 * LazyNoteEditor Component
 * 
 * Dynamically loads the appropriate editor component based on note type.
 */
const LazyNoteEditor = ({ 
  note, 
  onSave, 
  onCancel, 
  isInTab = false, 
  tabId = null,
  ...props 
}) => {
  const noteType = useMemo(() => detectNoteType(note), [note])
  
  // Get the appropriate editor component
  const EditorComponent = useMemo(() => {
    switch (noteType) {
      case 'FLOWCHART':
        return FlowchartNoteEditor
      case 'HANDWRITING':
        return HandwritingNoteEditor
      case 'VOICE':
        return VoiceNoteEditor
      case 'ITEM_LIST':
        return ItemListNoteEditor
      case 'PHOTO_NOTE':
        return PhotoNoteEditor
      case 'SPREADSHEET':
        return SpreadsheetNoteEditor
    default:
      // This should not happen since standard notes use CreateNotePage/EditNotePage
      throw new Error(`Unknown note type: ${noteType}`)
    }
  }, [noteType])

  // Log editor loading for debugging
  useEffect(() => {
    console.log(`📝 Loading editor for note type: ${noteType}`, {
      noteId: note?.noteId,
      editorComponent: getEditorComponentName(noteType)
    })
  }, [noteType, note?.noteId])

  return (
    <EditorErrorBoundary>
      <Suspense fallback={<EditorLoadingSkeleton />}>
        <EditorComponent
          note={note}
          noteType={noteType}
          onSave={onSave}
          onCancel={onCancel}
          isInTab={isInTab}
          tabId={tabId}
          {...props}
        />
      </Suspense>
    </EditorErrorBoundary>
  )
}

export default LazyNoteEditor
